import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@components/ui/table';
import { Download, Search, Loader2, ShieldAlert, Bug } from 'lucide-react';
import { getSubmissions, checkAdminAccess, exportSubmissionsCSV } from '@generated/server/admin';
import { debugSubitemColumns } from '@generated/server/enquiries';

export const Route = createFileRoute('/_app/submissions')({
  component: SubmissionsPage
});

function SubmissionsPage() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [debugResult, setDebugResult] = useState<any[] | null>(null);
  const [debugging, setDebugging] = useState(false);
  const [debugItemId, setDebugItemId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const accessCheck = await checkAdminAccess();
      setIsAdmin(accessCheck.isAdmin);
      
      if (!accessCheck.isAdmin) {
        setLoading(false);
        return;
      }
      
      const data = await getSubmissions();
      setSubmissions(data.items || []);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter((sub) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      sub.name?.toLowerCase().includes(search) ||
      sub.email?.toLowerCase().includes(search) ||
      sub.enquiryType?.toLowerCase().includes(search)
    );
  });

  const partialCount = filteredSubmissions.filter(sub => 
    sub.enquiryStatus?.includes('Partial')
  ).length;

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const csvData = await exportSubmissionsCSV();
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submissions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="max-w-md text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-10 w-10 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">
            Access Denied
          </h1>
          <p className="text-muted-foreground mb-6">
            This page is restricted to account administrators only. Please contact your workspace admin if you need access.
          </p>
          <Button onClick={() => window.location.href = '/'}>
            Return to Form
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">
                Submissions Log
              </h1>
              <p className="text-sm text-muted-foreground">
                {filteredSubmissions.length} total submissions
                {partialCount > 0 && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                    {partialCount} partial
                  </span>
                )}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting || filteredSubmissions.length === 0}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>

          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name, email, or enquiry type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Debug Panel */}
          <div className="mt-4 p-4 border border-border rounded-md bg-muted/30">
            <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <Bug className="h-4 w-4" /> Subitem Column Debug
            </p>
            {partialCount > 0 && (
              <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                <strong>Note:</strong> {partialCount} partial {partialCount === 1 ? 'entry' : 'entries'} detected. 
                These submissions were captured but failed to write to the monday.com board. All data is preserved in this log.
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <Input
                type="text"
                placeholder="Enter a parent item ID to test..."
                value={debugItemId}
                onChange={(e) => setDebugItemId(e.target.value)}
                className="max-w-xs text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={debugging || !debugItemId.trim()}
                onClick={async () => {
                  setDebugging(true);
                  setDebugResult(null);
                  try {
                    const res = await debugSubitemColumns({ data: { parentItemId: debugItemId.trim() } });
                    setDebugResult(res.results);
                  } catch (err: any) {
                    setDebugResult([{ step: 'Error', success: false, detail: err.message }]);
                  } finally {
                    setDebugging(false);
                  }
                }}
              >
                {debugging ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Bug className="h-4 w-4 mr-1" />}
                Run Test
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              This will create test subitems under the given item to identify which column formats work.
            </p>
            {debugResult && (
              <div className="mt-2 space-y-1 text-xs font-mono bg-background p-3 rounded border border-border overflow-auto max-h-64">
                {debugResult.map((r: any) => (
                  <div key={r.step} className={r.success ? 'text-green-700' : 'text-red-600'}>
                    {r.success ? '✅' : '❌'} <strong>{r.step}</strong>: {r.detail}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="border border-border rounded-[4px] bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Submitted</TableHead>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Contact</TableHead>
                  <TableHead className="font-semibold">Enquiry Type</TableHead>
                  <TableHead className="font-semibold">Event Date</TableHead>
                  <TableHead className="font-semibold">Venues</TableHead>
                  <TableHead className="font-semibold">Guests</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubmissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                      No submissions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubmissions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(sub.submittedAt)}
                      </TableCell>
                      <TableCell className="font-medium">{sub.name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {sub.email ? (
                          <a href={`mailto:${sub.email}`} className="text-primary hover:underline">
                            {sub.email}
                          </a>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {sub.contactNumber || '—'}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-[4px] px-2 py-1 text-xs font-medium bg-muted whitespace-nowrap">
                          {sub.specificEnquiryType || sub.enquiryType || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(sub.eventDate)}
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">
                        {sub.venuePreferences?.join(', ') || '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {sub.numberOfGuests || '—'}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-[4px] px-2 py-1 text-xs font-medium whitespace-nowrap ${
                          sub.enquiryStatus === 'Actioned' 
                            ? 'bg-green-100 text-green-800'
                            : sub.enquiryStatus?.includes('Partial')
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {sub.enquiryStatus || 'New Enquiry'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
